import { NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@df/core/app';
import { ImageModule } from '@df/module-image/image.module';
import { ColorOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { defaultImage } from 'projects/app-default/src/app/icms.config';
import SwiperCore, { Pagination, Swiper } from 'swiper';
import { SwiperModule } from 'swiper/angular';
import { SwiperEvents } from 'swiper/types';
import { getCmsContentFieldConfig } from '../../../../../cms/components/components.config';
import { ICmsComponentContentData, ICmsContentVariant, ICmsContentVideoProvider } from '../../../../../cms/components/components.interface';
import { SwatchComponent } from '../../../../attribute/swatch/swatch.component';
import { SwiperDotsComponent } from 'projects/app-default/src/app/ui/widget/swiper-dots/swiper-dots.component';
SwiperCore.use([Pagination]);
interface PageComponentColourSelectorComponentData extends NICmsContentComponent.IData, ICmsComponentContentData {
    variant: string;
    initialTitle?: string;
    title: string;
    subtitle?: string;
    default_image: string;
    defaultImageUrl?: string;
    colours: ColourSelectourComponentSwatchItemData[];
}
interface ColourSelectourComponentSwatchItemData {
    swatch: string;
    label: string;
    copy?: string;
    ctaCopy?: string;
    images: SwatchItemImageData[];
    url: string;
    new_copy?: boolean;
    new_copy_color?: string;
}
interface SwatchItemImageData {
    image: string;
}
@ICmsComponent<PageComponentColourSelectorComponent>({
    id: 'colour-selector',
    name: 'Colour collection selection',
    tags: ['colour', 'collection'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Variant',
            required: true,
            options: [
                { key: 'side-by-side', title: 'Side by side' },
                { key: 'image-above-text', title: 'Image above text' }
            ],
            default: 'side-by-side'
        },
        <NICmsContentField.IField>{
            key: 'initialTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Initial title'
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true,
            default: `What's Your Favourite Colour?`
        },
        <NICmsContentField.IField>{
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            default: 'Discover the LeMieux Colour Collections'
        },
        ...getCmsContentFieldConfig('default_image', 'Default image'),
        <NICmsContentField.IField>{
            key: 'defaultImageUrl',
            type: NICmsContentField.EFieldType.text,
            title: 'Colour url'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'colours',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Colour swatch',
            itemName: 'swatch',
            itemNameField: 'label',
            required: true,
            fields: [
                <NICmsContentField.IColorPickerField>{
                    key: 'swatch',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Swatch',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'label',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy'
                },
                <NICmsContentField.IField>{
                    key: 'ctaCopy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'CTA copy'
                },
                <NICmsContentField.IField>{
                    key: 'url',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Colour url',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'images',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Images',
                    itemName: 'image',
                    required: true,
                    fields: [
                        <NICmsContentField.IField>{
                            key: 'image',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Image'
                        }
                    ],
                    defaultSize: 1
                },
                <NICmsContentField.IField>{
                    key: 'new_copy',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Toggle "new" copy'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'new_copy_color',
                    type: NICmsContentField.EFieldType.choice,
                    title: '"New" copy color',
                    options: ColorOptions,
                    previewMode: 'color',
                    canShow: data => data.new_copy
                }
            ],
            default: [
                {
                    name: 'Benetton Blue',
                    swatch: 'rgba(54, 70, 94, 1)',
                    images: [
                        {
                            image: defaultImage
                        }
                    ],
                    url: '/',
                    label: 'Atlantic',
                    new_copy: true,
                    new_copy_color: 'col-w'
                },
                {
                    name: 'Benetton Blue',
                    swatch: 'rgba(32, 59, 61, 1)',
                    images: [
                        {
                            image: defaultImage
                        }
                    ],
                    url: '/',
                    label: 'Spruce'
                },
                {
                    name: 'Benetton Blue',
                    swatch: 'rgba(136, 81, 87, 1)',
                    images: [
                        {
                            image: defaultImage
                        }
                    ],
                    url: '/',
                    label: 'Orchid'
                },
                {
                    name: 'Benetton Blue',
                    swatch: 'rgba(0, 0, 0, 1)',
                    images: [
                        {
                            image: defaultImage
                        }
                    ],
                    url: '/',
                    label: 'Black'
                }
            ]
        }
    ]
})
@Component({
    selector: 'page-component-colour-selector',
    templateUrl: './colour-selector.html',
    styleUrls: ['./colour-selector.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        HrefComponent,
        ICmsClientModule,
        ImageModule,
        NgClass,
        NgFor,
        NgIf,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        NgTemplateOutlet,
        ShellComponent,
        SizeClassDirective,
        SwatchComponent,
        SwiperModule,
        SwiperDotsComponent,
        TranslationModule,
        VimeoPlayerComponent,
        YoutubePlayerComponent
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentColourSelectorComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: PageComponentColourSelectorComponentData;
    contentVariant = ICmsContentVariant;
    contentVideoProvider = ICmsContentVideoProvider;
    protected router = inject(Router);

    protected app = inject(App);

    swiper?: Swiper;
    private _selectedColorIndex: number | undefined;
    get selectedColorIndex(): number | undefined {
        return this._selectedColorIndex;
    }
    set selectedColorIndex(value: number | undefined) {
        const was = this.selectedColorIndex;
        this._selectedColorIndex = value;
        if (was !== value) {
            this.swiper?.slideTo(0);
        }
        this.detectChanges();
    }
    get activeColourData(): ColourSelectourComponentSwatchItemData | undefined {
        if (this.selectedColorIndex !== undefined) {
            return this.component?.colours[this.selectedColorIndex];
        }
        return undefined;
    }
    get activeImages(): string[] {
        let images: string[] = [];
        if (this.selectedColorIndex !== undefined) {
            const color = this.component?.colours?.[this.selectedColorIndex];
            if (color?.images) {
                images = color.images.map(i => i.image);
            }
        } else {
            images = [this.component?.default_image];
        }
        return images.filter(i => !!i);
    }
    changeActiveImage($event: Event, index?: number): void {
        if (($event.type === 'mouseleave' || $event.type === 'mouseenter') && !this.app.client.isTouchy) {
            // On desktop mouseenter event will only change image
            this.selectedColorIndex = index;
        }
        if ($event.type === 'click' && this.app.client.isTouchy) {
            // First tap on mobile will only change image
            if (index !== undefined && this.selectedColorIndex === index) {
                // Second tap should redirect user
                const url = this.component?.colours[index].url;
                if (url) {
                    this.router.navigateByUrl(url);
                }
            }
            this.selectedColorIndex = index;
        } else if ($event.type === 'click') {
            //On desktop click event should redirect user
            const url = index !== undefined ? this.component?.colours?.[index]?.url : undefined;
            if (url) {
                this.router.navigateByUrl(url);
            }
        }
        this.detectChanges();
    }
    onAfterInit(eventParams: Parameters<SwiperEvents['afterInit']>) {
        const [swiper] = eventParams;
        this.swiper = swiper;
        this.detectChanges();
    }
}
